/**
 * Плавающая панель над выделенными фото папки — как `SelectionBar` пинов:
 * внизу рабочей области, по центру относительно `main`.
 *
 * Действия: «В папку ▾» (существующая или новая), «Сделать обложкой» и «Искать по
 * этому фото» (только при одном выделенном), «Снять ручную привязку» — фото снова `auto`, папку им
 * поменяет только следующий `photos cluster`.
 *
 * Сама панель ничего не знает о кэше: мутации даёт экран папки.
 */
import { ChevronDown, FolderInput, Search, Star, Undo2, X } from "lucide-react";
import { useCallback, useState, type MouseEvent } from "react";

import { DressPicker } from "@/components/photos/DressPicker";
import { btn, btnGhost, ErrorLine } from "@/components/trends/ui";
import { num } from "@/lib/media";
import type { DressCard } from "@/lib/photos";

export function PhotoSelectionBar({
  count,
  visibleCount,
  onSelectVisible,
  onClear,
  dresses,
  currentDressId,
  onMove,
  onCover,
  onLens,
  onReset,
  busy,
  error,
}: {
  count: number;
  visibleCount: number;
  onSelectVisible: () => void;
  onClear: () => void;
  dresses: DressCard[];
  currentDressId: number;
  /** `null` — в новую папку. */
  onMove: (dressId: number | null) => Promise<void>;
  /** Есть, только когда выделено одно фото. */
  onCover: (() => Promise<void>) | null;
  /** Искать похожие в Pinterest по этому фото; есть, только когда выделено одно. */
  onLens: (() => Promise<void>) | null;
  onReset: () => Promise<void>;
  busy: boolean;
  error: unknown;
}) {
  const [picking, setPicking] = useState(false);
  const close = useCallback(() => setPicking(false), []);
  // Клик по кнопке открытого поповера не должен сначала закрыть его «кликом мимо».
  const keep = (e: MouseEvent) => e.stopPropagation();

  const move = (dressId: number | null) => void onMove(dressId).then(close, () => undefined);

  return (
    <div className="pointer-events-none fixed right-0 bottom-4 left-[var(--spacing-sidebar)] z-40 flex justify-center px-4">
      <div
        className="pointer-events-auto flex max-w-full flex-wrap items-center gap-2 rounded-[var(--radius-card)] border border-border bg-bg-elevated px-3 py-2 text-[12.5px] shadow-xl"
        data-annot="панель выделения фото"
      >
        <span className="font-medium text-fg tnum">Выделено {num(count)}</span>
        <button
          type="button"
          className={btnGhost}
          onClick={onSelectVisible}
          disabled={visibleCount === 0}
          title="Выделить все фото папки"
        >
          все ({num(visibleCount)})
        </button>
        <button type="button" className={btnGhost} onClick={onClear} title="Снять выделение (Esc)">
          <X size={12} />
          снять
        </button>

        <span className="h-5 w-px bg-border" />

        <div className="relative">
          <button
            type="button"
            className={btn}
            aria-expanded={picking}
            onMouseDown={keep}
            onClick={() => setPicking((v) => !v)}
            disabled={busy}
          >
            <FolderInput size={13} />В папку
            <ChevronDown size={12} />
          </button>
          {picking ? (
            <DressPicker
              dresses={dresses}
              exclude={currentDressId}
              allowNew
              onPick={move}
              onClose={close}
              up
            />
          ) : null}
        </div>
        {onCover && count === 1 ? (
          <button
            type="button"
            className={btn}
            onClick={() => void onCover().catch(() => undefined)}
            disabled={busy}
          >
            <Star size={13} />
            Сделать обложкой
          </button>
        ) : null}
        {onLens && count === 1 ? (
          <button
            type="button"
            className={btn}
            onClick={() => void onLens().catch(() => undefined)}
            disabled={busy}
            title="Найти похожие в Pinterest по этому фото"
          >
            <Search size={13} />
            Искать по этому фото
          </button>
        ) : null}
        <button
          type="button"
          className={btn}
          onClick={() => void onReset().catch(() => undefined)}
          disabled={busy}
          title="Фото снова «авто»: следующий прогон кластеризации сможет переложить их в другую папку"
        >
          <Undo2 size={13} />
          Снять ручную привязку
        </button>
        {error ? (
          <div className="basis-full">
            <ErrorLine error={error} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
